import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  Pipe,
  PipeTransform,
  SimpleChanges,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { RouterLink } from '@angular/router';
import { I18NextModule } from 'angular-i18next';
import { map, Observable } from 'rxjs';
import { places, regions } from 'utils/places-utility';
import { RouteTranslateService } from '../../../../common/route-translate/route-translate.service';
import { SearchBannerFragment, SearchBannerGQL } from './search-banner.query.generated';

@Pipe({
  name: 'sharryRelatedFindVisible',
  standalone: true,
})
export class SharryRelatedFindVisiblePipe implements PipeTransform {
  transform(value: SearchBannerFragment[]): boolean {
    return value.some(b => b.visible);
  }
}

@Pipe({
  name: 'sharryRelatedVisible',
  standalone: true,
})
export class SharryRelatedVisiblePipe implements PipeTransform {
  transform(value: readonly SearchBannerFragment[], start: number, end?: number): SearchBannerFragment[] {
    return value.filter(b => b.visible).slice(start, end);
  }
}

@Pipe({
  name: 'sharryBannerRedirect',
  standalone: true,
})
export class SharryBannerRedirectPipe implements PipeTransform {
  constructor(private routeTranslateService: RouteTranslateService) {}

  transform(value: SearchBannerFragment): string {
    if (value.categoryId) {
      return `${this.routeTranslateService.translateRoute('category')}/${value.category?.normalizedName}`;
    } else if (Object.keys(regions).includes(value.keyword)) {
      return `${this.routeTranslateService.translateRoute('find/what-to-do-in')}/${value.keyword}`;
    } else if (Object.keys(places).includes(value.keyword)) {
      return `${this.routeTranslateService.translateRoute('find/what-to-do-at')}/${value.keyword}`;
    } else {
      return `${this.routeTranslateService.translateRoute('discover')}/${value.keyword}`;
    }
  }
}

@Component({
  selector: 'sharry-search-banner',
  standalone: true,
  imports: [
    NgClass,
    NgStyle,
    AsyncPipe,
    MatExpansionModule,
    SharryRelatedFindVisiblePipe,
    SharryRelatedVisiblePipe,
    I18NextModule,
    MatButtonModule,
    RouterLink,
    SharryBannerRedirectPipe,
  ],
  templateUrl: './search-banner.component.html',
  styleUrls: ['./search-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchBannerComponent implements OnChanges {
  @Input() searchTerm: string;
  @Input() searchKind: 'id' | 'keyword' | 'categoryId';

  banner$: Observable<SearchBannerFragment>;
  currentIndex = -1;
  bannerImageLoaded = false;

  constructor(
    private searchBannerGQL: SearchBannerGQL,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.searchTerm.currentValue) {
      const input = {
        id: this.searchKind === 'id' ? this.searchTerm : undefined,
        keyword: this.searchKind === 'keyword' ? this.searchTerm : undefined,
        categoryId: this.searchKind === 'categoryId' ? this.searchTerm : undefined,
      };
      this.banner$ = this.searchBannerGQL
        .fetch({ input }, { fetchPolicy: 'cache-first' })
        .pipe(map(r => r.data.searchBanner));
    }
  }

  openChange(i: number) {
    this.currentIndex = i;
    this.cd.detectChanges();
  }

  onImageLoad() {
    this.bannerImageLoaded = true;
    this.cd.detectChanges();
  }
}
